import { useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  type FreshStrawPregCheckImportResponse,
  useImportFreshStrawPregCheckMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface FreshStrawPregCheckImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultSiteName?: string;
  onImported?: (result: FreshStrawPregCheckImportResponse) => void;
}

export function FreshStrawPregCheckImportDialog({
  open,
  onOpenChange,
  defaultSiteName,
  onImported,
}: FreshStrawPregCheckImportDialogProps) {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [siteName, setSiteName] = useState('');
  const [faria, setFaria] = useState(false);
  const [result, setResult] =
    useState<FreshStrawPregCheckImportResponse | null>(null);

  const [importPregCheck, { isLoading }] =
    useImportFreshStrawPregCheckMutation();

  useEffect(() => {
    if (open) {
      setFile(null);
      setSiteName(defaultSiteName ?? '');
      setFaria(false);
      setResult(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open, defaultSiteName]);

  const handleSubmit = async () => {
    if (!file) return;
    try {
      const response = await importPregCheck({
        file,
        defaultSiteName: siteName || undefined,
        faria,
      }).unwrap();

      setResult(response);
      onImported?.(response);

      const errorCount = response.errors?.length ?? 0;
      dispatch(
        addToast({
          severity: errorCount > 0 ? 'warn' : 'success',
          summary: 'Preg-check import complete',
          detail: `Parsed ${response.parsed}, saved ${response.saved}${
            errorCount > 0
              ? `, ${errorCount} error${errorCount === 1 ? '' : 's'}`
              : ''
          }.`,
          life: ToastLife.LONG,
        }),
      );
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Import failed',
          detail: err instanceof Error ? err.message : String(err),
          life: ToastLife.LONG,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import preg-check results</DialogTitle>
          <DialogDescription>
            Upload an Excel workbook of preg-check results. Standard exports
            have <code>DairyCompID</code>, <code>PCDate</code>, and{' '}
            <code>PCResult</code> columns. Tick <strong>FARIA format</strong> if
            the file uses the DCC Dairy Comp extract — those rows are treated as
            &quot;Preg&quot; automatically.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fresh-straw-pregcheck-file">Workbook (.xlsx)</Label>
            <Input
              ref={fileInputRef}
              id="fresh-straw-pregcheck-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fresh-straw-pregcheck-site">
              Default site name{' '}
              <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="fresh-straw-pregcheck-site"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              placeholder="Site disambiguation hint"
              disabled={isLoading}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={faria}
              onCheckedChange={(checked) => setFaria(checked === true)}
              disabled={isLoading}
              aria-label="FARIA format"
            />
            <span>FARIA Dairy Comp format (all rows scored as Preg)</span>
          </label>

          {result && (
            <div
              className="border-border bg-muted/30 rounded-md border p-3 text-sm"
              data-testid="fresh-straw-pregcheck-result"
            >
              <div className="font-semibold">
                Parsed {result.parsed} · Saved {result.saved}
              </div>
              {result.errors?.length > 0 && (
                <ul className="text-destructive mt-2 list-disc space-y-0.5 pl-5 text-xs">
                  {result.errors.slice(0, 10).map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                  {result.errors.length > 10 && (
                    <li>…and {result.errors.length - 10} more</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!file || isLoading}
          >
            {isLoading ? 'Importing…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
